import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { SERVICE_HISTORY_HEADING_ID, ServiceHistory } from '@/components/customers/ServiceHistory';
import { AddPropertyDialog } from '@/components/customers/AddPropertyDialog';
import { LeadSourceField } from '@/components/customers/LeadSourceField';
import { NewJobDialog } from '@/components/customers/NewJobDialog';
import { EditCustomerDialog } from '@/components/customers/EditCustomerDialog';
import { ArchiveCustomerDialog } from '@/components/customers/ArchiveCustomerDialog';
import { useSetCustomerLeadSource } from './customers/useCustomerActions';
import { AppointmentEmailsSwitch } from '@/components/appointments/AppointmentEmailsSwitch';
import { useSetCustomerAppointmentEmails } from '@/routes/appointments/useAppointmentEmails';
import { useAccount } from '@/api/useAccount';
import { useHasPermission } from '@/auth/RequirePermission';
import { Permissions } from '@/auth/permissions';
import { NotFound } from './NotFound';
import {
  CustomerNotFoundError,
  useCustomer,
  useCustomerJobs,
  useCustomerProperties,
} from './customers/useCustomers';

/**
 * One customer: who they are, where they are, and everything ever done for
 * them (M1-04).
 *
 * <b>Three independent queries, not one aggregate.</b> The contact block is
 * what someone on the phone needs first and it should not wait on a job
 * history; the history is gated on a permission the contact details are not.
 * Each section carries its own loading and error state for the same reason.
 *
 * <b>What the AC asked for and this does not do.</b> An amount per job, an
 * outstanding balance, and a "new estimate" quick action all need money in the
 * data model, and there is none — the invoice entity is M2-11, estimates are
 * M2-05, both on M2-04's shared line item. They are absent rather than
 * mocked up: a balance of "$0.00" on a page where no invoice can exist is a
 * number someone will eventually believe.
 */
export function CustomerDetail() {
  const { t } = useTranslation();
  const { customerId } = useParams<{ customerId: string }>();
  const hasPermission = useHasPermission();

  const canReadJobs = hasPermission(Permissions.readAllJobs);
  const canManage = hasPermission(Permissions.manageCustomers);
  const canCreateJobs = hasPermission(Permissions.assignJobs);

  const account = useAccount();
  const customer = useCustomer(customerId);
  const properties = useCustomerProperties(customerId);
  const jobs = useCustomerJobs(customerId, canReadJobs);

  const [dialog, setDialog] = useState<'property' | 'job' | 'edit' | 'archive' | null>(null);
  // A polite live region rather than a toast: the created row appears in the
  // list right below, and someone who cannot see it appear still needs telling.
  const [announcement, setAnnouncement] = useState('');
  const setLeadSource = useSetCustomerLeadSource(customerId ?? '');
  const setAppointmentEmails = useSetCustomerAppointmentEmails(customerId ?? '');

  // A stale link is not an error, it is a missing page — and it should look
  // like every other missing page in the app rather than like a failed request.
  if (customer.error instanceof CustomerNotFoundError) {
    return <NotFound />;
  }

  if (customer.isPending) {
    return <LoadingState label={t('customerDetail.loading')} />;
  }

  if (customer.isError) {
    return (
      <ErrorState
        title={t('customerDetail.loadErrorTitle')}
        description={t('customerDetail.loadErrorDescription')}
        onRetry={() => void customer.refetch()}
      />
    );
  }

  const record = customer.data;
  const activeProperties = (properties.data ?? []).filter(
    (property) => property.status !== 'Archived',
  );

  return (
    <section className="space-y-6">
      <div className="space-y-1">
        <p className="text-sm">
          <Link
            to="/customers"
            className="text-[var(--color-muted-foreground)] underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
          >
            {t('customerDetail.backToCustomers')}
          </Link>
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">{record.name}</h1>
          {/* Words, not a colour: an archived customer reads as archived to
              everyone. */}
          {record.status === 'Archived' ? (
            <span className="rounded-full border border-[var(--color-destructive)] px-2 py-0.5 text-xs font-medium text-[var(--color-destructive)]">
              {t('customers.status.Archived')}
            </span>
          ) : null}
        </div>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          {t(`customers.type.${record.type}`, { defaultValue: record.type })}
        </p>
      </div>

      {/* ---- Quick actions ------------------------------------------------ */}
      {canManage || canCreateJobs ? (
        <div className="flex flex-wrap gap-2">
          {canCreateJobs ? (
            <Button type="button" size="sm" onClick={() => setDialog('job')}>
              {t('customerDetail.newJob.action')}
            </Button>
          ) : null}
          {canManage ? (
            <Button type="button" variant="outline" size="sm" onClick={() => setDialog('property')}>
              {t('customerDetail.addProperty.action')}
            </Button>
          ) : null}
          {canManage ? (
            <Button type="button" variant="outline" size="sm" onClick={() => setDialog('edit')}>
              {t('customers.editCustomer.action')}
            </Button>
          ) : null}
          {/* Hidden once already archived (M1-30): a repeat "Archive" on an
              archived customer is idempotent on the API but reads as
              confusing UI, not as a genuinely safe-to-repeat action. */}
          {canManage && record.status !== 'Archived' ? (
            <Button type="button" variant="outline" size="sm" onClick={() => setDialog('archive')}>
              {t('customers.archiveCustomer.action')}
            </Button>
          ) : null}
        </div>
      ) : null}

      {/* ---- Contact ------------------------------------------------------ */}
      <div className="rounded-lg border p-4">
        <h2 className="text-base font-semibold">{t('customerDetail.contactHeading')}</h2>
        <dl className="mt-3 grid gap-x-6 gap-y-3 sm:grid-cols-2">
          <div>
            <dt className="text-xs text-[var(--color-muted-foreground)]">
              {t('customerDetail.primaryContact')}
            </dt>
            <dd>{record.primaryContact ?? t('customerDetail.none')}</dd>
          </div>
          <div>
            <dt className="text-xs text-[var(--color-muted-foreground)]">
              {t('customerDetail.phone')}
            </dt>
            {/* Real links: on a phone this page IS the dial pad, and a
                technician reading it in a van should not be retyping digits. */}
            <dd>
              {record.phone ? (
                <a className="underline-offset-2 hover:underline" href={`tel:${record.phone}`}>
                  {record.phone}
                </a>
              ) : (
                t('customerDetail.none')
              )}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-[var(--color-muted-foreground)]">
              {t('customerDetail.email')}
            </dt>
            <dd>
              {record.email ? (
                <a className="underline-offset-2 hover:underline" href={`mailto:${record.email}`}>
                  {record.email}
                </a>
              ) : (
                t('customerDetail.none')
              )}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-[var(--color-muted-foreground)]">
              {t('customerDetail.billingAddress')}
            </dt>
            <dd>
              <address className="not-italic">
                {record.billingAddress.line1}
                {record.billingAddress.line2 ? `, ${record.billingAddress.line2}` : ''}
                <br />
                {record.billingAddress.city}, {record.billingAddress.state}{' '}
                {record.billingAddress.postalCode}
              </address>
            </dd>
          </div>
          {record.notes ? (
            <div className="sm:col-span-2">
              <dt className="text-xs text-[var(--color-muted-foreground)]">
                {t('customerDetail.notes')}
              </dt>
              <dd className="whitespace-pre-line">{record.notes}</dd>
            </div>
          ) : null}
        </dl>
      </div>

      {/* ---- How they found us --------------------------------------------- */}
      {/*
          M1-45. M1-28 shipped this field end to end on the API and built the
          catalog screen for the vocabulary, but nothing that could read an
          attribution back — so it was written once, in AddCustomerDialog, and
          then invisible and uncorrectable for the life of the record.

          ⚠️ Not in the Edit dialog, and not on PUT /customers/{id}. See
          useSetCustomerLeadSource: it lived there once and silently erased the
          attribution on every unrelated edit.
      */}
      <div className="rounded-lg border p-4">
        <h2 className="text-base font-semibold">{t('customerDetail.leadSourceHeading')}</h2>
        <div className="mt-3">
          <LeadSourceField
            leadSourceId={record.leadSourceId}
            leadSourceLabel={record.leadSourceLabel}
            leadSourceDetail={record.leadSourceDetail}
            canManage={canManage}
            onSave={(leadSourceId, leadSourceDetail) =>
              setLeadSource.mutateAsync({ leadSourceId, leadSourceDetail })
            }
            onSaved={(label) =>
              setAnnouncement(
                label
                  ? t('customerDetail.leadSourceSaved', { label })
                  : t('customerDetail.leadSourceCleared'),
              )
            }
          />
        </div>
      </div>

      {/* ---- Appointment emails (M2-21) ------------------------------------ */}
      <div className="rounded-lg border p-4">
        <h2 className="text-base font-semibold">{t('appointmentEmails.switch.heading')}</h2>
        <div className="mt-3">
          <AppointmentEmailsSwitch
            enabled={record.appointmentEmailsEnabled}
            lastChange={record.appointmentEmailsLastChange}
            canManage={canManage}
            recipient="customer"
            name={record.name}
            isPending={setAppointmentEmails.isPending}
            onToggle={(enabled) => setAppointmentEmails.mutateAsync(enabled)}
            testId="customer-appointment-emails"
          />
        </div>
      </div>

      {/* ---- Properties --------------------------------------------------- */}
      <div className="space-y-3">
        <h2 className="text-base font-semibold">{t('customerDetail.propertiesHeading')}</h2>

        {properties.isPending ? (
          <LoadingState label={t('customerDetail.propertiesLoading')} />
        ) : properties.isError ? (
          <ErrorState
            title={t('customerDetail.propertiesErrorTitle')}
            description={t('customerDetail.propertiesErrorDescription')}
            onRetry={() => void properties.refetch()}
          />
        ) : properties.data.length === 0 ? (
          <EmptyState
            title={t('customerDetail.noPropertiesTitle')}
            description={t('customerDetail.noPropertiesDescription')}
            action={
              canManage ? (
                <Button type="button" size="sm" onClick={() => setDialog('property')}>
                  {t('customerDetail.addProperty.action')}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <ul className="grid gap-3 sm:grid-cols-2">
            {properties.data.map((property) => (
              <li key={property.id} data-testid="property-card">
                {/* The whole card, not just the address: M1-05's detail page
                    is what a technician standing at the door and an office
                    user editing the gate code both actually want, and there
                    is nothing else on this card worth clicking separately. */}
                <Link
                  to={`/properties/${property.id}`}
                  className="block rounded-lg border p-3 text-sm hover:bg-[var(--color-muted)] focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
                >
                  <address className="font-medium not-italic">
                    {property.address.line1}
                    {property.unit ? `, ${property.unit}` : ''}
                    <br />
                    <span className="font-normal text-[var(--color-muted-foreground)]">
                      {property.address.city}, {property.address.state} {property.address.postalCode}
                    </span>
                  </address>
                  {property.gateCode ? (
                    <p className="mt-2 text-xs">
                      <span className="text-[var(--color-muted-foreground)]">
                        {t('customerDetail.gateCode')}:{' '}
                      </span>
                      {property.gateCode}
                    </p>
                  ) : null}
                  {property.accessNotes ? (
                    <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                      {property.accessNotes}
                    </p>
                  ) : null}
                  {property.status === 'Archived' ? (
                    <p className="mt-2 text-xs font-medium">{t('customerDetail.propertyArchived')}</p>
                  ) : null}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ---- Service history ---------------------------------------------- */}
      <div className="space-y-3">
        {/* The id is what names the scroll region inside `ServiceHistory`
            (M1-42) — one string for the heading and the region, so they
            cannot drift apart. */}
        <h2 id={SERVICE_HISTORY_HEADING_ID} className="text-base font-semibold">
          {t('customerDetail.historyHeading')}
        </h2>

        {!canReadJobs ? (
          // Not an error: this user was never entitled to it, and an error box
          // would read as a broken page rather than a narrower one.
          <p className="text-sm text-[var(--color-muted-foreground)]">
            {t('customerDetail.historyNotPermitted')}
          </p>
        ) : jobs.isPending ? (
          <LoadingState label={t('customerDetail.historyLoading')} />
        ) : jobs.isError ? (
          <ErrorState
            title={t('customerDetail.historyErrorTitle')}
            description={t('customerDetail.historyErrorDescription')}
            onRetry={() => void jobs.refetch()}
          />
        ) : jobs.data.length === 0 ? (
          <EmptyState
            title={t('customerDetail.noHistoryTitle')}
            description={t('customerDetail.noHistoryDescription')}
          />
        ) : (
          <ServiceHistory
            jobs={jobs.data}
            properties={properties.data ?? []}
            timeZone={account.data?.timezone}
          />
        )}
      </div>

      <p role="status" aria-live="polite" className="sr-only" data-testid="customer-announcement">
        {announcement}
      </p>

      {dialog === 'property' && customerId ? (
        <AddPropertyDialog
          customerId={customerId}
          onClose={() => setDialog(null)}
          onAdded={(line1) => setAnnouncement(t('customerDetail.addProperty.added', { line1 }))}
        />
      ) : null}

      {dialog === 'job' && customerId ? (
        <NewJobDialog
          customerId={customerId}
          properties={activeProperties}
          defaultLeadSourceId={record.leadSourceId}
          defaultLeadSourceDetail={record.leadSourceDetail}
          onClose={() => setDialog(null)}
          onCreated={(description) =>
            setAnnouncement(t('customerDetail.newJob.created', { description }))
          }
        />
      ) : null}

      {dialog === 'edit' ? (
        <EditCustomerDialog
          customer={record}
          onClose={() => setDialog(null)}
          onSaved={() => setAnnouncement(t('customers.editCustomer.saved', { name: record.name }))}
        />
      ) : null}

      {dialog === 'archive' && customerId ? (
        <ArchiveCustomerDialog
          customerId={customerId}
          customerName={record.name}
          onClose={() => setDialog(null)}
          onArchived={() =>
            setAnnouncement(t('customers.archiveCustomer.archived', { name: record.name }))
          }
        />
      ) : null}
    </section>
  );
}
